// src/pages/RecycleBinPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { dashboardApi } from '@/services/dashboardApi';
import { DashboardGroups } from '@/components/DashboardGroups/DashboardGroups';
import { SearchBar } from '@/components/Filter/SearchBar';
import { useInfoWindow } from '@/contexts/InfoWindowContext';
import type { DashboardGroupData } from '@/components/DashboardGroups/types';
import type { Conversation, ConversationDetail } from '@/types/conversation';

export const RecycleBinPage: React.FC = () =>
{
  const [initialData, setInitialData] = useState<DashboardGroupData[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const { openInfoWindow } = useInfoWindow();

  useEffect(() =>
  {
    document.title = 'Recycle Bin';
  }, []);

  const handleLoadMoreCards = useCallback(
    async (_groupId: number, page: number): Promise<Conversation[]> =>
    {
      const groups = await dashboardApi.getRecycleConversations({
        CustomFields: [],
        PageNumber: page,
        PageSize: 100,
        DashboardId: 1,
        Filters: '[]',
        LayersToHide: '',
      });
      const firstGroup = groups?.[0];
      return (firstGroup?.Conversations ?? []) as unknown as Conversation[];
    },
    [],
  );

  const loadRecycleBin = useCallback(async () =>
  {
    setLoading(true);
    setError(null);
    try
    {
      const groups = await dashboardApi.getRecycleConversations({
        CustomFields: [],
        PageNumber: 1,
        PageSize: 100,
        DashboardId: 1,
        Filters: '[]',
        LayersToHide: '',
      });
      setInitialData(groups as unknown as DashboardGroupData[]);
    }
    catch (err)
    {
      console.error('Error loading recycle bin:', err);
      setError('Error loading recycle bin items.');
    }
    finally
    {
      setLoading(false);
    }
  }, []);

  useEffect(() =>
  {
    loadRecycleBin();
  }, [loadRecycleBin]);

  const handleOpenInfoWindow = useCallback(
    (conversation: ConversationDetail) =>
    {
      openInfoWindow(conversation.ConversationId, true);
    },
    [openInfoWindow],
  );

  return (
    <div className="recycle-bin-page h-full flex flex-col">
      {/* Header + Search */}
      <div className="flex items-center gap-2 px-4 py-4 bg-[#ededed] dark:bg-[#2d2f2f]">
        <SearchBar filterSearchCard={searchText} onChangeSearchFilterCards={setSearchText} />
      </div>

      {/* Body */}
      {loading && (
        <div className="flex items-center justify-center flex-1">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5dade2]" />
        </div>
      )}
      {!loading && error && <div className="px-6 py-8 text-sm text-red-500">{error}</div>}
      {!loading && !error && (
        <div className="flex-1 min-h-0 overflow-hidden">
          <DashboardGroups
            dashboardId={1}
            initialData={initialData ?? []}
            recycleBin={true}
            searchText={searchText}
            cardViewMode="compact"
            dashboardViewTypeId={1}
            columnWidth={350}
            onOpenInfoWindow={handleOpenInfoWindow}
            onLoadMoreCards={handleLoadMoreCards}
          />
        </div>
      )}
    </div>
  );
};

export default RecycleBinPage;
